import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { merge, omit } from 'lodash';
import { getTenantConfig } from '@services/tenantConfigService';
import { SELL_API } from '@services/axiosInstance';
import type {
  CustomerThemeOverrides,
  TenantBranding,
  TenantConfigResponse,
  TenantFeatures,
} from '@services/tenantConfig.types';
import {
  DEFAULT_BRANDING,
  DEFAULT_FEATURES,
  DEFAULT_THEME_OVERRIDES,
} from '@config/theme/tenantThemeDefaults';

export const tenantConfigQueryKey = ['tenantConfig'] as const;

/** Tenant config after the API response is deep-merged over the Novus defaults. */
export type ResolvedTenantConfig = {
  themeOverrides: CustomerThemeOverrides;
  branding: TenantBranding;
  features: TenantFeatures;
  hasEvOfferings: boolean;
  tmfEnabled: boolean;
};

/** The value used before the fetch resolves or when it fails — pure defaults. */
export const DEFAULT_RESOLVED_CONFIG: ResolvedTenantConfig = {
  themeOverrides: DEFAULT_THEME_OVERRIDES,
  branding: DEFAULT_BRANDING,
  features: DEFAULT_FEATURES,
  hasEvOfferings: false,
  tmfEnabled: false,
};

// Editor metadata carried alongside the theme tokens — not consumed by the mapper.
const THEME_METADATA_KEYS = ['branding', 'componentFonts', 'name', 'description', 'createdAt', 'updatedAt'] as const;

const BRANDING_ASSET_FIELDS = ['logoSrc', 'heroImage', 'faviconUrl'] as const;
const FONT_URL_FIELDS = ['displayFontUrl', 'headerFontUrl', 'bodyFontUrl', 'monoFontUrl'] as const;
export const UUID_EXT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[a-zA-Z0-9]{1,10}$/i;

// SELL_API.defaults.baseURL reflects the runtime apiBaseUrl (prop or env fallback,
// see SellConfigContext) — NOT import.meta.env.VITE_SELL_BASE_URL, which is baked
// in at sell_mfe's own build time and is empty/wrong once consumed as a pre-built
// package by a host app that configures the base URL at runtime instead.
const buildAssetUrl = (fileRef: string): string => {
  const base = SELL_API.defaults.baseURL ?? '';
  const baseUrl = base.endsWith('/') ? base : `${base}/`;
  return `${baseUrl}branding/asset/${fileRef}`;
};

// Replaces UUID.ext branding values with their sell_backend proxy URL so components
// can use them directly as <img src> without any extra fetching logic.
const resolveBrandingAssets = (branding: TenantBranding): TenantBranding => {
  const base = SELL_API.defaults.baseURL ?? '';
  if (!base) return branding;

  const resolved = { ...branding } as Record<string, unknown>;
  for (const field of BRANDING_ASSET_FIELDS) {
    const val = resolved[field];
    if (typeof val === 'string' && UUID_EXT_RE.test(val)) {
      resolved[field] = buildAssetUrl(val);
    }
  }
  return resolved as TenantBranding;
};

// Replaces UUID.ext font file references with their sell_backend proxy URL so
// useDynamicFonts can register them via the FontFace API.
const resolveFontUrls = (themeOverrides: CustomerThemeOverrides): CustomerThemeOverrides => {
  const base = SELL_API.defaults.baseURL ?? '';
  if (!base) return themeOverrides;

  const fontStacks = (themeOverrides as Record<string, unknown>).fontStacks;
  if (!fontStacks || typeof fontStacks !== 'object') return themeOverrides;

  const original = fontStacks as Record<string, unknown>;
  const resolvedEntries = FONT_URL_FIELDS.map((field): [string, string] | null => {
    const val = original[field];
    return typeof val === 'string' && UUID_EXT_RE.test(val) ? [field, buildAssetUrl(val)] : null;
  }).filter((entry): entry is [string, string] => entry !== null);

  if (resolvedEntries.length === 0) return themeOverrides;

  return {
    ...themeOverrides,
    fontStacks: { ...original, ...Object.fromEntries(resolvedEntries) },
  } as CustomerThemeOverrides;
};

// Split branding out of the profile and drop metadata, then deep-merge each slice
// over its default so a partial API payload only overrides the keys it provides.
const resolveTenantConfig = (data: TenantConfigResponse): ResolvedTenantConfig => {
  const profile = data.themeConfig ?? {};
  const themeOnly = omit(profile, THEME_METADATA_KEYS) as CustomerThemeOverrides;
  const rawBranding = merge({}, DEFAULT_BRANDING, profile.branding) as TenantBranding;

  const rawThemeOverrides = merge({}, DEFAULT_THEME_OVERRIDES, themeOnly) as CustomerThemeOverrides;

  return {
    themeOverrides: resolveFontUrls(rawThemeOverrides),
    branding: resolveBrandingAssets(rawBranding),
    features: merge({}, DEFAULT_FEATURES, data.features) as TenantFeatures,
    hasEvOfferings: data.hasEvOfferings ?? false,
    tmfEnabled: data.tmfEnabled ?? false,
  };
};

/**
 * Fetches the tenant branding + theme once at app init and resolves it against
 * the Novus defaults. Fired inside the provider stack below QueryClientProvider;
 * on error the caller falls back to {@link DEFAULT_RESOLVED_CONFIG}.
 */
export const useTenantConfig = (): UseQueryResult<ResolvedTenantConfig, Error> =>
  useQuery({
    queryKey: tenantConfigQueryKey,
    queryFn: getTenantConfig,
    select: resolveTenantConfig,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
